/**
 * The area a focus region, a haze region or an annotation shape covers: what the editor's shared
 * shape picker writes. The three features stay separate (their own lists, looks and budgets);
 * only the way each one says "where" is the same.
 *
 * - `shape`: 'sphere' (an ellipsoid when its size is unequal), 'box', or 'outline' (clicked onto
 *   the splat surface and extruded `depth` in front of and behind it, see outline-shape.ts).
 * - `apply`: 'inside' applies the feature inside the shape; 'outside' applies it everywhere
 *   except inside the shape. Several shapes of one feature (or one annotation layer) combine:
 *   inside shapes add their areas, outside shapes each cut their own area out of "everywhere".
 * - Edge softness (`feather`) and, for outlines, thickness (`depth`).
 *
 * COORDINATE FRAMES. Sphere / box fields (position, rotation, size) are in the editor's
 * Z-mirrored scene coordinates, like every RegionShape (regionMatrix negates z). Outline
 * `points` are runtime scene coordinates, the space surface picks and measurements use. The
 * conversions below are exact, so a shape converted from one kind to another (or the same
 * values used by another feature) covers the same splats.
 *
 * UNITS. Sphere / box feather is a fraction of the half-size (0..1), as before. Outline feather
 * and depth are scene units, as annotation outlines have always stored them.
 *
 * Shader cost model (unchanged): sphere / box transforms ride in uniforms, so moving or resizing
 * them never recompiles; outlines are baked into the shader source, so editing one recompiles once.
 *
 * SPLAT OBJECTS. `shape: 'splats'` selects whole splat objects (`splatIds`: the main scan,
 * Separate results, added splats) instead of a drawn area: a splat is inside when it belongs to
 * one of them, wherever the object is moved or animated (splat-objects.ts). Edge softness doesn't
 * apply. The sphere / box fields keep the box around the objects when they were picked: what a
 * viewer that predates splat objects draws instead (it reads the unknown kind as that ellipsoid).
 */
import * as pc from 'playcanvas';
import { DEFAULT_REGION, normalizeRegion, regionWeight, type RegionShape } from './region-shape';
import { outlinePoints, outlineSlabDefaults, outlineSlabWeight, resolveOutlineSlab, type OutlineSlab } from './outline-shape';
import { normalizeSplatObjectIds } from './splat-objects';
import type { SurfacePoint } from '../utils/surfacePolygon';

export type AreaShapeKind = RegionShape['shape'] | 'outline' | 'splats';
export type AreaApply = 'inside' | 'outside';

export interface AreaShape extends Omit<RegionShape, 'shape'> {
    /** 'sphere' | 'box' use position / rotation / size; 'outline' uses `points`. Unknown kinds read as 'sphere'. */
    shape: AreaShapeKind;
    /**
     * The outline, in order, in runtime scene coordinates (the same space as measurements and
     * annotation shapes, NOT the Z-mirrored coordinates of position). Outline shapes only.
     */
    points?: SurfacePoint[];
    /** Outline thickness: how far in front of and behind the surface it reaches, scene units. Default from the outline size. */
    depth?: number;
    /** Apply the feature inside the shape (default) or everywhere outside it. */
    apply?: AreaApply;
    /** 'splats' shapes: the splat objects selected, by stable id (MAIN_SPLAT_OBJECT_ID or a custom mesh id). */
    splatIds?: string[];
}

export const AREA_SHAPE_KINDS: readonly AreaShapeKind[] = ['sphere', 'box', 'outline', 'splats'];

export function areaApply(value: string | undefined, fallback: AreaApply = 'inside'): AreaApply {
    return value === 'outside' || value === 'inside' ? value : fallback;
}

/** A shape ready to draw: a sphere / box region (editor coordinates) or an outline slab (runtime coordinates). */
export type ResolvedArea =
    | { kind: RegionShape['shape']; region: RegionShape; apply: AreaApply }
    | { kind: 'outline'; slab: OutlineSlab; points: SurfacePoint[]; apply: AreaApply }
    | { kind: 'splats'; ids: string[]; apply: AreaApply };

/**
 * Validated copy of a stored shape. Null for an outline that can't be drawn yet (fewer than three
 * points, all on a line) or a splat object selection with no objects: it covers nothing, so
 * features leave it out like a disabled one.
 */
export function resolveArea(value: Partial<AreaShape> | null | undefined, defaultApply: AreaApply = 'inside'): ResolvedArea | null {
    if (!value || typeof value !== 'object') return null;
    const apply = areaApply(value.apply, defaultApply);
    if (value.shape === 'splats') {
        const ids = normalizeSplatObjectIds(value.splatIds);
        return ids.length ? { kind: 'splats', ids, apply } : null;
    }
    if (value.shape === 'outline') {
        const slab = resolveOutlineSlab(value.points, value.depth, value.feather);
        return slab ? { kind: 'outline', slab, points: outlinePoints(value.points), apply } : null;
    }
    const region = normalizeRegion({ ...value, shape: value.shape === 'box' ? 'box' : 'sphere' });
    return { kind: region.shape, region, apply };
}

/** Runtime scene coordinates <-> the editor's Z-mirrored ones (the mirror is its own inverse). */
export const mirrorZ = (p: SurfacePoint): SurfacePoint => ({ x: p.x, y: p.y, z: -p.z });

/**
 * How much a shape covers a point (0..1) before `apply`; the point is in runtime scene coordinates.
 * A splat object selection covers the point when `objectId` (the splat object the point belongs
 * to) is one of its objects.
 */
export function areaCoverage(area: ResolvedArea, point: SurfacePoint, objectId?: string | null): number {
    if (area.kind === 'splats') return objectId && area.ids.includes(objectId) ? 1 : 0;
    return area.kind === 'outline' ? outlineSlabWeight(area.slab, point) : regionWeight(area.region, mirrorZ(point));
}

/** CPU twin of the shader: the shape's weight after `apply` (outside = 1 - coverage). */
export function areaWeight(area: ResolvedArea, point: SurfacePoint, objectId?: string | null): number {
    const w = areaCoverage(area, point, objectId);
    return area.apply === 'outside' ? 1 - w : w;
}

/**
 * Where several shapes of one feature apply, from their weights after `apply`: inside shapes
 * combine as a union (the largest weight); outside shapes each keep only what lies outside them,
 * so together they keep what lies outside all of them (the smallest weight). With both kinds a
 * point counts when either says so. CPU twin of combineAreaShader.
 */
export function combineAreaWeights(weights: number[], applies: AreaApply[]): number {
    const inside = weights.filter((_, i) => applies[i] !== 'outside');
    const outside = weights.filter((_, i) => applies[i] === 'outside');
    const a = inside.length ? Math.max(...inside) : null;
    const b = outside.length ? Math.min(...outside) : null;
    return a === null ? (b ?? 0) : b === null ? a : Math.max(a, b);
}

/**
 * Shader expression for combineAreaWeights over `terms` (each already a weight after `apply`).
 * Only inside shapes prints exactly the `max(max(a, b), c)` chain the effects have always used,
 * so scenes without outside shapes compile to the same arithmetic. Same text in GLSL and WGSL.
 */
export function combineAreaShader(terms: string[], applies: AreaApply[]): string {
    const inside = terms.filter((_, i) => applies[i] !== 'outside');
    const outside = terms.filter((_, i) => applies[i] === 'outside');
    const chain = (list: string[], fn: string) => list.reduce((a, b) => `${fn}(${a}, ${b})`);
    if (!outside.length) return inside.length ? chain(inside, 'max') : '0.0';
    if (!inside.length) return chain(outside, 'min');
    return `max(${chain(inside, 'max')}, ${chain(outside, 'min')})`;
}

/** `term` after `apply`: the coverage for inside, `(1.0 - coverage)` for outside. */
export const applyAreaShader = (coverage: string, apply: AreaApply): string => (apply === 'outside' ? `(1.0 - ${coverage})` : coverage);

/**
 * The box (editor coordinates) an outline's slab fills: oriented with the outline's plane,
 * spanning the outline in the plane and `depth` either side of it. What a sphere / box converted
 * from the outline covers, and what viewers that predate outlines draw instead (they read any
 * unknown shape as a sphere with these fields). Null when the outline can't be drawn.
 */
export function outlineBoundsRegion(points: ReadonlyArray<SurfacePoint> | undefined, depth?: number): Pick<RegionShape, 'position' | 'rotation' | 'size'> | null {
    const slab = resolveOutlineSlab(points, depth, 0);
    if (!slab) return null;
    const { fit } = slab;
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    for (const p of fit.outline) {
        minU = Math.min(minU, p.u);
        maxU = Math.max(maxU, p.u);
        minV = Math.min(minV, p.v);
        maxV = Math.max(maxV, p.v);
    }
    const cu = (minU + maxU) / 2;
    const cv = (minV + maxV) / 2;
    const center = {
        x: fit.origin.x + fit.u.x * cu + fit.v.x * cv,
        y: fit.origin.y + fit.u.y * cu + fit.v.y * cv,
        z: fit.origin.z + fit.u.z * cu + fit.v.z * cv,
    };
    // The plane frame (u, v, normal) is right-handed and orthonormal: the runtime rotation of the
    // box. regionMatrix builds the runtime rotation from editor angles as (-x, -y, z).
    const m = new pc.Mat4();
    m.data.set([fit.u.x, fit.u.y, fit.u.z, 0, fit.v.x, fit.v.y, fit.v.z, 0, fit.normal.x, fit.normal.y, fit.normal.z, 0, 0, 0, 0, 1]);
    const euler = new pc.Quat().setFromMat4(m).getEulerAngles();
    const clean = (v: number) => (Math.abs(v) < 1e-9 ? 0 : v);
    return {
        position: mirrorZ(center),
        rotation: { x: clean(-euler.x), y: clean(-euler.y), z: clean(euler.z) },
        size: { x: Math.max(0.01, maxU - minU), y: Math.max(0.01, maxV - minV), z: Math.max(0.01, slab.depth * 2) },
    };
}

/**
 * What to store when an outline is drawn (or redrawn): its points, the thickness and soft edge a
 * new outline starts with, and its bounding box in the sphere / box fields (for converting it
 * and for older viewers). Null when the points don't make an outline.
 */
export function outlineAreaPatch(points: SurfacePoint[]): Pick<AreaShape, 'shape' | 'points' | 'depth' | 'feather' | 'position' | 'rotation' | 'size'> | null {
    const slab = resolveOutlineSlab(points, undefined, undefined);
    const bounds = slab && outlineBoundsRegion(points, slab.depth);
    if (!slab || !bounds) return null;
    const { depth, feather } = outlineSlabDefaults(slab.fit);
    return { shape: 'outline', points: points.map((p) => ({ x: p.x, y: p.y, z: p.z })), depth, feather, ...bounds };
}

/**
 * The patch that turns a stored shape into `kind`, keeping it in place: an outline becomes the
 * sphere / box around it (its points are dropped; switching back means drawing again); a sphere
 * and a box swap freely; becoming an outline keeps any outline already stored, otherwise it
 * waits for one to be drawn. A splat object selection keeps its objects (`splatIds`) and the box
 * around them, so switching to a sphere / box starts from that box and switching back finds the
 * same objects. Becoming a splat object selection drops any outline points too (switching back
 * means drawing again): viewers that predate splat objects read an annotation shape of an unknown
 * kind as an outline and would draw them. Edge softness is reset when its unit changes
 * (fraction <-> scene units).
 */
export function convertAreaShape(value: Partial<AreaShape>, kind: AreaShapeKind): Partial<AreaShape> {
    const from: AreaShapeKind = value.shape === 'outline' || value.shape === 'box' || value.shape === 'splats' ? value.shape : 'sphere';
    if (from === kind) return {};
    if (kind === 'splats') return { shape: 'splats', splatIds: normalizeSplatObjectIds(value.splatIds), ...(value.points?.length ? { points: [] } : {}) };
    if (kind === 'outline') {
        const slab = resolveOutlineSlab(value.points, value.depth, undefined);
        return { shape: 'outline', feather: slab ? outlineSlabDefaults(slab.fit).feather : 0 };
    }
    if (from !== 'outline') return { shape: kind, ...(from === 'splats' ? { feather: DEFAULT_REGION.feather } : {}) };
    const bounds = outlineBoundsRegion(value.points, value.depth);
    return { shape: kind, ...(bounds ?? {}), feather: DEFAULT_REGION.feather, points: [] };
}

/**
 * What the editor draws as a shape's bounds: the sphere / box wireframe, the outline itself
 * (runtime coordinates), or each selected splat object's own box.
 */
export type AreaBounds = RegionShape | { shape: 'outline'; points: SurfacePoint[] } | { shape: 'splats'; ids: string[] };

/** Bounds the editor draws for an effect, optionally in a colour of their own (rgb 0..1; a region's). */
export type EditorAreaBounds = AreaBounds & { color?: readonly [number, number, number] };

export function areaBounds(area: ResolvedArea): AreaBounds {
    if (area.kind === 'splats') return { shape: 'splats', ids: [...area.ids] };
    return area.kind === 'outline'
        ? { shape: 'outline', points: area.points }
        : { ...area.region, position: { ...area.region.position }, rotation: { ...area.region.rotation }, size: { ...area.region.size } };
}

/**
 * The entries of a focus / haze region list an effect draws, in uniform slot order: enabled
 * ones whose shape can be drawn, at most `max`. The viewer keeps its slot bookkeeping with the
 * same function, so slot i is always entry i.
 */
export function drawableAreas<T extends { enabled?: boolean }>(
    list: ReadonlyArray<T | null | undefined> | undefined,
    max: number,
    defaultApply: AreaApply = 'inside',
): Array<{ item: T; area: ResolvedArea }> {
    const out: Array<{ item: T; area: ResolvedArea }> = [];
    for (const item of Array.isArray(list) ? list : []) {
        if (out.length >= max) break;
        if (!item || typeof item !== 'object' || item.enabled === false) continue;
        const area = resolveArea(item as Partial<AreaShape>, defaultApply);
        if (area) out.push({ item, area });
    }
    return out;
}
